/**
 * usePatientSections.ts
 *
 * Which surfaces of the Patients module a practice has switched off.
 *
 * The Patients module cannot be disabled — it is the product — so instead of an
 * on/off switch it carries a list of SECTIONS a practice does not want. A
 * peptides-only clinic has no use for referral sources, plans or marketing
 * consent, and every one of those on screen is a field someone has to decide
 * to ignore.
 *
 * The slugs are a CONTRACT with Prospera-Corp-App's module cog
 * (resources/js/pages/AdminModuleSettings.jsx, MODULE_SECTIONS.patients), which
 * is where they get switched off. They are not routes and never appear in a
 * URL. Renaming one here silently un-hides that surface for every practice that
 * had it off, so add rather than rename.
 */
import { useMemo } from 'react';
import { useModuleAccess } from '@/hooks/useAccountModules';

/** Every hideable surface of the Patients module. Keep in step with the cog. */
export const PATIENT_SECTIONS = {
  // ── The list page ───────────────────────────────────────────────────────
  filters: 'filters',
  tags: 'tags',
  referralSource: 'referral-source',
  plans: 'plans',
  createPlan: 'create-plan',
  sendSms: 'send-sms',
  sendEmail: 'send-email',

  // ── The slide-over that opens from a row ────────────────────────────────
  panelTotalSpend: 'panel-total-spend',
  panelAppointments: 'panel-appointments',
  panelNoShows: 'panel-no-shows',
  panelPlans: 'panel-plans',
  panelTags: 'panel-tags',
  panelNote: 'panel-note',
  panelMembership: 'panel-membership',
  panelRecentVisits: 'panel-recent-visits',
  panelUpcomingAppointments: 'panel-upcoming-appointments',

  // ── The create / edit form ──────────────────────────────────────────────
  formTransactionalSms: 'form-transactional-sms',
  formMarketingSms: 'form-marketing-sms',
  formTransactionalEmail: 'form-transactional-email',
  formMarketingEmail: 'form-marketing-email',
  formPlanSetup: 'form-plan-setup',
  formInterests: 'form-interests',

  // ── The patient detail page, /patients/{id} ─────────────────────────────
  detailEmailConsent: 'detail-email-consent',
  detailSmsConsent: 'detail-sms-consent',
  detailProducts: 'detail-products',
  detailMemberships: 'memberships',
  detailUpcomingAppointments: 'detail-upcoming-appointments',
  detailAppStatus: 'app-status',
  detailAppointments: 'detail-appointments',
  detailNoShows: 'detail-no-shows',
  detailLastAppointment: 'detail-last-appointment',
  detailComped: 'detail-comped',
} as const;

export type PatientSection = (typeof PATIENT_SECTIONS)[keyof typeof PATIENT_SECTIONS];

export interface PatientSectionAccess {
  /** True when this practice has switched the surface off. */
  hidden: (section: PatientSection) => boolean;
  /** Inverse of `hidden`, for the common `{show(...) && <X/>}` shape. */
  show: (section: PatientSection) => boolean;
  isLoading: boolean;
}

export function usePatientSections(): PatientSectionAccess {
  const { disabledSections, isLoading } = useModuleAccess('patients');

  return useMemo(() => {
    const off = new Set(disabledSections);
    // Fails OPEN, like the rest of the module system: while the list is
    // loading, or if it never arrives, everything renders. A surface that
    // flickers away is worse than one that stays.
    const hidden = (section: PatientSection) => off.has(section);
    return { hidden, show: (s: PatientSection) => !hidden(s), isLoading };
  }, [disabledSections, isLoading]);
}
